import { Link } from "react-router-dom";
import {
  ClipboardList,
  MailCheck,
  CalendarCheck,
  AlertCircle,
  ArrowRight,
} from "lucide-react";

const steps = [
  {
    icon: ClipboardList,
    title: "Join the interest list",
    text: "Email us your player's name and any helpful notes — that's all we need to get started.",
  },
  {
    icon: MailCheck,
    title: "We'll follow up by email",
    text: "Once registration opens, we'll reach out with next steps and full details so you know what to expect.",
  },
  {
    icon: CalendarCheck,
    title: "Show up and play",
    text: "Bring sneakers and water — we'll take care of the rest.",
  },
];

export default function Register() {
  return (
    <div data-testid="register-page">
      <section className="basketball-bg-accent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-16 pb-10 md:pt-24 md:pb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-50 text-orange-700 text-sm font-extrabold uppercase tracking-wider">
            <AlertCircle className="w-4 h-4" />
            Launching soon — join our interest list
          </span>
          <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 leading-[1.05] max-w-3xl">
            Reserve your spot at{" "}
            <span className="text-blue-600">UHL.</span>
          </h1>
          <p className="mt-5 text-lg font-medium leading-relaxed text-slate-600 max-w-2xl">
            Registration opens soon — join our interest list below to be the
            first to hear when we launch. We'll follow up by email with next
            steps and full details.
          </p>
        </div>
      </section>

      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map(({ icon: Icon, title, text }, i) => (
              <div
                key={title}
                data-testid={`register-step-${i}`}
                className="relative bg-white rounded-3xl p-8 border border-slate-100 shadow-xl shadow-blue-900/5"
              >
                <span className="absolute -top-3 left-6 bg-blue-600 text-white text-xs font-black uppercase tracking-wider px-3 py-1 rounded-full">
                  Step {i + 1}
                </span>
                <div className="w-14 h-14 rounded-2xl bg-orange-100 flex items-center justify-center">
                  <Icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="mt-5 text-xl font-black text-slate-900">
                  {title}
                </h3>
                <p className="mt-2 text-slate-600 font-medium leading-relaxed">
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOOGLE FORM EMBED PLACEHOLDER */}
      <section className="py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div
            data-testid="register-form-embed"
            className="relative rounded-3xl bg-white border border-slate-100 shadow-xl shadow-blue-900/5 p-10 md:p-14 text-center"
          >
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
              <ClipboardList className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="mt-6 text-2xl sm:text-3xl font-black text-slate-900">
              Registration opens soon
            </h3>
            <p className="mt-3 text-slate-600 font-medium max-w-xl mx-auto">
              We're getting ready to launch! Join our interest list below to
              be the first to hear when registration opens — or reach out
              with any questions.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Reserve%20My%20Spot%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20I%27d%20like%20to%20reserve%20a%20spot%20for%20my%20player.%20Please%20follow%20up%20with%20next%20steps%20when%20registration%20opens.%0A%0AParent%20name%3A%20%0APlayer%27s%20name%20%26%20age%3A%20%0AAny%20notes%3A%20"
                data-testid="register-reserve-btn"
                className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white hover:bg-orange-600 hover:shadow-lg hover:-translate-y-0.5 transition-all font-bold px-7 py-3.5 rounded-full"
              >
                📩 Reserve Your Spot
              </a>
              <a
                href="mailto:unifiedhoopsleague@gmail.com?subject=Join%20Interest%20List%20%E2%80%94%20UHL&body=Hi%20UHL%2C%20please%20add%20me%20to%20your%20interest%20list%20and%20share%20updates%20when%20registration%20opens.%0A%0AName%3A%20%0APlayer%27s%20name%20%26%20age%3A%20%0AQuestions%3A%20"
                data-testid="register-interest-btn"
                className="inline-flex items-center justify-center gap-2 border-2 border-blue-600 text-blue-600 hover:bg-blue-50 transition-all font-bold px-7 py-3.5 rounded-full"
              >
                📩 Join Interest List
              </a>
            </div>

            <p className="mt-6 text-sm text-slate-500 font-semibold">
              Or write us anytime at{" "}
              <a
                href="mailto:unifiedhoopsleague@gmail.com"
                className="text-blue-600 underline font-bold"
                data-testid="register-email-fallback"
              >
                unifiedhoopsleague@gmail.com
              </a>
            </p>
          </div>

          <div className="mt-10 bg-orange-50 rounded-3xl p-8 border border-orange-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <p className="font-black text-slate-900">
                Don't forget your parent forms.
              </p>
              <p className="text-slate-600 font-medium">
                After registering, download and return the waiver, medical
                info, and media release forms.
              </p>
            </div>
            <Link
              to="/forms"
              data-testid="register-to-forms-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white hover:bg-orange-600 transition-all font-bold px-6 py-3 rounded-full"
            >
              Get Forms <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
